// src/components/dashboards/charts/TreatmentTreemap.tsx
// Treemap: therapy type distribution with response breakdown.

import React from "react";
import { Box, Skeleton, Typography } from "@mui/material";
import ReactECharts from "echarts-for-react";
import useChartTheme from "./useChartTheme";
import { TreatmentsData } from "hooks/useAnalytics";
import { useTranslation } from "../../../i18n";

interface Props {
  data: TreatmentsData | null;
  loading: boolean;
}

const TreatmentTreemap: React.FC<Props> = ({ data, loading }) => {
  const { baseTooltip, seriesColors, textPrimary, chartHeight } = useChartTheme();
  const { t } = useTranslation();

  if (loading || !data) {
    return <Skeleton variant="rounded" height={chartHeight} sx={{ borderRadius: 2 }} />;
  }

  const items = data.therapy_types;
  if (!items || items.length === 0) {
    return (
      <Box sx={{ height: chartHeight, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Typography variant="body2" color="text.secondary">
          {t("dashboard.analytics.no_treatment_data", "No treatment data available")}
        </Typography>
      </Box>
    );
  }

  const treemapData = items.map((item, i) => ({
    name: item.name.charAt(0).toUpperCase() + item.name.slice(1),
    value: item.value,
    itemStyle: { color: seriesColors[i % seriesColors.length] },
  }));

  const option = {
    title: {
      text: t("dashboard.analytics.treatment_distribution", "Treatment Distribution"),
      textStyle: { color: textPrimary, fontSize: 14, fontWeight: 600 },
      left: 0,
    },
    tooltip: {
      ...baseTooltip,
      formatter: (params: any) => `${params.name}: ${params.value} events`,
    },
    series: [
      {
        type: "treemap",
        data: treemapData,
        roam: false,
        breadcrumb: { show: false },
        label: { show: true, fontSize: 12, color: "#fff", fontWeight: 500 },
        itemStyle: { borderColor: "rgba(255,255,255,0.3)", borderWidth: 2, gapWidth: 2 },
        levels: [
          {
            itemStyle: { borderColor: "rgba(255,255,255,0.4)", borderWidth: 3, gapWidth: 3 },
          },
        ],
      },
    ],
  };

  return (
    <Box sx={{ width: "100%", height: chartHeight }}>
      <ReactECharts option={option} style={{ width: "100%", height: "100%" }} opts={{ renderer: "canvas" }} />
    </Box>
  );
};

export default TreatmentTreemap;
